import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BodyHighlight, Description, Subtitle } from "@ddm-design-system/typography";
import Button from "../../../../common/button/Button";
import Modal from "../../../../common/modal/Modal";
import { LoadingIcon } from "../../../../common/data_table/dataTable.styles";
import {
  applyX10Config,
  getX10ConfigHistory as requestX10ConfigHistory,
  pingX10,
  startX10ConfigPolling,
  stopX10ConfigPolling
} from "../../../../../store/x10config/actions";
import {
  getIsApplyingX10Config,
  getX10ConfigApplyBlocked,
  getX10ConfigHistory,
  getX10ConfigLastPing,
  getX10ConfigLoadError,
  getX10Configuration
} from "../../../../../store/x10config/selectors";
import {
  IX10ConfigChange,
  IX10ConfigurationUpdate,
  X10ConfigBlockedReason,
  X10ConfigItemStatus
} from "../../../../../store/x10config/types";
import ConfigurationHistory from "./ConfigurationHistory";
import {
  ApplyBar,
  Banner,
  VolumeSpinner,
  Chip,
  Dot,
  Help,
  Meta,
  Section,
  SectionHeader
} from "./configuration.styles";
import {
  AMBIENT_OFFSET_LIMIT,
  blockedText,
  buildUpdate,
  clamp,
  currentValues,
  formatLiters,
  formatOffset,
  IX10ConfigDraft,
  roundToStep,
  STEP,
  VESSEL_OFFSET_LIMIT,
  VOLUME_MAX_LITERS,
  VOLUME_MIN_LITERS
} from "./helpers";
import OffsetControl from "./OffsetControl";
import OnOffSwitch from "./OnOffSwitch";
import SyncStatus, { formatAgo, formatDateTime } from "./SyncStatus";
import useConfigurationText from "./useConfigurationText";

/** Reasons the user cannot do anything about from this tab: the controls are read-only. */
const READ_ONLY_REASONS: X10ConfigBlockedReason[] = [
  "FEATURE_DISABLED",
  "READ_ONLY_ROLE",
  "NOT_PROVISIONED"
];
/** While there are unsent edits, ping again once the last heartbeat gets this old, so Apply stays possible. */
const KEEP_VERIFIED_AFTER_SECONDS = 90;
const PING_COOLDOWN_MS = 65000;
/** A change still on its way: show what was requested rather than what the unit reported before it. */
const PENDING_STATUSES: X10ConfigItemStatus[] = ["PENDING", "SENT", "UNCONFIRMED", "DELIVERED"];

const pendingValue = (change: IX10ConfigChange | null) =>
  change && PENDING_STATUSES.includes(change.status) ? change.value : undefined;

interface IProps {
  componentId: string;
}

const Configuration: React.FC<IProps> = ({ componentId }) => {
  const t = useConfigurationText();
  const dispatch = useDispatch();
  const configuration = useSelector(getX10Configuration(componentId));
  const history = useSelector(getX10ConfigHistory(componentId));
  const loadError = useSelector(getX10ConfigLoadError);
  const applying = useSelector(getIsApplyingX10Config);
  const applyBlocked = useSelector(getX10ConfigApplyBlocked);
  const lastPing = useSelector(getX10ConfigLastPing);
  const [draft, setDraft] = useState<IX10ConfigDraft>({});
  const [confirming, setConfirming] = useState(false);
  const lastPingAt = useRef(Date.now());
  const wasApplying = useRef(false);

  useEffect(() => {
    lastPingAt.current = Date.now();
    dispatch(startX10ConfigPolling(componentId));
    return () => {
      dispatch(stopX10ConfigPolling());
    };
  }, [dispatch, componentId]);

  const update: IX10ConfigurationUpdate = useMemo(
    () => (configuration ? buildUpdate(draft, configuration) : {}),
    [draft, configuration]
  );
  const dirty = Object.keys(update).length > 0;

  // Keep the unit verified online while the user is preparing a change.
  const lastHeard = configuration?.online.lastHeardSecondsAgo;
  const canPing = !!configuration?.supports.ping;
  useEffect(() => {
    if (
      !dirty ||
      !canPing ||
      (lastHeard !== null && lastHeard !== undefined && lastHeard < KEEP_VERIFIED_AFTER_SECONDS)
    ) {
      return;
    }
    if (Date.now() - lastPingAt.current < PING_COOLDOWN_MS) {
      return;
    }
    lastPingAt.current = Date.now();
    dispatch(pingX10(componentId));
  }, [dirty, canPing, lastHeard, dispatch, componentId]);

  // Refresh the history whenever a tracked status changes.
  const historySignature = configuration
    ? [
        configuration.inFlight?.id,
        configuration.inFlight?.status,
        configuration.vesselOffset.lastChange?.status,
        configuration.ambientOffset.lastChange?.status,
        configuration.halo.lastChange?.status,
        configuration.coolingFailMode.lastChange?.status,
        configuration.volume.lastChange?.status
      ].join("|")
    : "";
  useEffect(() => {
    if (historySignature) {
      dispatch(requestX10ConfigHistory(componentId));
    }
  }, [historySignature, dispatch, componentId]);

  // A change that went out clears the edits; a refused one keeps them for another try.
  useEffect(() => {
    if (wasApplying.current && !applying && !applyBlocked) {
      setDraft({});
    }
    wasApplying.current = applying;
  }, [applying, applyBlocked]);

  if (!configuration) {
    return loadError ? (
      <div className="pt-xl">{t("load_error", "The configuration could not be loaded.")}</div>
    ) : (
      <div className="w-full flex items-center justify-center pt-xl">
        <LoadingIcon />
      </div>
    );
  }

  const current = currentValues(configuration);
  const reason = configuration.blockedReason;
  const readOnly = !!reason && READ_ONLY_REASONS.includes(reason);
  const controlsDisabled = readOnly || applying;
  const { online, supports } = configuration;
  const checking = !online.verified && !!lastPing?.pinged;
  const onOffText = (value: boolean | null | undefined) =>
    value === null || value === undefined
      ? t("unknown", "unknown")
      : value
      ? t("on", "On")
      : t("off", "Off");
  const failModeReported = configuration.coolingFailMode.reported;
  const failModePending = pendingValue(configuration.coolingFailMode.lastChange) as
    | boolean
    | undefined;
  const failModeReportedAt = configuration.coolingFailMode.reportedAt;
  const failModeText = [
    `${t("unit_reports", "The unit reports")}: ${onOffText(failModeReported)}`,
    failModeReportedAt ? ` (${t("since", "since")} ${formatDateTime(failModeReportedAt)})` : "",
    failModePending !== undefined && failModePending !== failModeReported
      ? ` · ${t("requested", "requested")}: ${onOffText(failModePending)}`
      : ""
  ].join("");
  const volumePending = pendingValue(configuration.volume.lastChange) as number | undefined;
  const cooldownText = `${t("ping_cooldown", "You can check again in")} ${
    lastPing?.retryAfterSeconds ?? 60
  } s`;
  const kegNowText = `${t("keg_now", "Keg now")}: ${formatLiters(configuration.volume.kegLiters)}`;

  const onlineBanner = online.verified ? (
    <Banner tone="success">
      <span className="inline-flex items-center gap-xs">
        <Dot tone="success" />
        {`${t("online", "Online")} · ${t("heard_from", "heard from")} ${formatAgo(
          online.lastHeardSecondsAgo
        )} ${t("ago", "ago")}`}
      </span>
      <Description>{`${t("firmware", "Firmware")} ${configuration.firmware || "--"}`}</Description>
    </Banner>
  ) : (
    <Banner tone={checking ? "neutral" : "warning"}>
      <span className="inline-flex flex-col">
        <span className="inline-flex items-center gap-xs">
          <Dot tone={checking ? "neutral" : "warning"} />
          {checking
            ? t("checking", "Checking that the unit is online…")
            : `${t("not_verified", "Not heard from recently")} · ${t("last_heard", "last heard")} ${
                online.lastHeardAt ? formatDateTime(online.lastHeardAt) : "--"
              }`}
        </span>
        <Meta>
          {t(
            blockedText("OFFLINE_OR_UNVERIFIED", null).key,
            blockedText("OFFLINE_OR_UNVERIFIED", null).fallback
          )}
        </Meta>
        {lastPing?.reason === "COOLDOWN" && <Meta>{cooldownText}</Meta>}
      </span>
      {supports.ping && !readOnly && (
        <Button
          secondary
          disabled={checking}
          onClick={() => {
            lastPingAt.current = Date.now();
            dispatch(pingX10(componentId));
          }}
        >
          {t("check_again", "Check again")}
        </Button>
      )}
    </Banner>
  );

  const shownReason = applyBlocked?.reason || reason;
  const reasonBanner =
    shownReason && shownReason !== "OFFLINE_OR_UNVERIFIED" ? (
      <Banner tone={readOnly ? "neutral" : "warning"}>
        {applyBlocked?.message && !readOnly
          ? applyBlocked.message
          : t(
              blockedText(shownReason, configuration.retryAfterSeconds).key,
              blockedText(shownReason, configuration.retryAfterSeconds).fallback
            )}
      </Banner>
    ) : null;

  const changes: string[] = [];
  if (update.vesselOffset !== undefined) {
    changes.push(
      `${t("vessel_offset", "Vessel probe offset")}: ${formatOffset(
        current.vesselOffset
      )} → ${formatOffset(update.vesselOffset)}`
    );
  }
  if (update.ambientOffset !== undefined) {
    changes.push(
      `${t("ambient_offset", "Ambient probe offset")}: ${formatOffset(
        current.ambientOffset
      )} → ${formatOffset(update.ambientOffset)}`
    );
  }
  if (update.coolingFailMode !== undefined) {
    changes.push(
      `${t("fail_mode", "Cooling fail mode")}: ${
        update.coolingFailMode ? t("on", "On") : t("off", "Off")
      }`
    );
  }
  if (update.halo !== undefined) {
    changes.push(`${t("halo", "Halo light")}: ${update.halo ? t("on", "On") : t("off", "Off")}`);
  }
  if (update.volumeLeftLiters !== undefined) {
    changes.push(
      `${t("volume", "Keg volume")}: ${formatLiters(
        configuration.volume.kegLiters
      )} → ${formatLiters(update.volumeLeftLiters)}`
    );
  }

  const pendingText = `${changes.length} ${t("changes_pending", "unsent change(s)")}`;

  const send = () => {
    setConfirming(false);
    dispatch(applyX10Config(componentId, update));
  };

  return (
    <div className="pt-lg" data-testid="x10-configuration">
      {onlineBanner}
      {reasonBanner}

      <Section>
        <SectionHeader>
          <BodyHighlight>{t("vessel_offset", "Vessel probe offset")}</BodyHighlight>
          <SyncStatus configKey="V_OFFSET" change={configuration.vesselOffset.lastChange} />
        </SectionHeader>
        <Help>
          {t(
            "vessel_offset_help",
            "Corrects the vessel temperature reading, from -3 to +3 °C. The unit does not report its offset back: it is sent only while the unit is online, and re-sent after every restart."
          )}
        </Help>
        {supports.offsets ? (
          <OffsetControl
            id="x10-vessel-offset"
            label={t("vessel_offset", "Vessel probe offset")}
            limit={VESSEL_OFFSET_LIMIT}
            current={current.vesselOffset}
            value={draft.vesselOffset}
            disabled={controlsDisabled}
            onChange={value => setDraft({ ...draft, vesselOffset: value })}
          />
        ) : (
          <Meta>
            {t(
              blockedText("FIRMWARE_UNSUPPORTED", null).key,
              blockedText("FIRMWARE_UNSUPPORTED", null).fallback
            )}
          </Meta>
        )}
      </Section>

      <Section>
        <SectionHeader>
          <BodyHighlight>{t("ambient_offset", "Ambient probe offset")}</BodyHighlight>
          <SyncStatus configKey="A_OFFSET" change={configuration.ambientOffset.lastChange} />
        </SectionHeader>
        <Help>
          {t(
            "ambient_offset_help",
            "Corrects the ambient temperature reading, from -5 to +5 °C. In fail mode the unit cools from the ambient temperature, so this offset matters most when the vessel probe is faulty."
          )}
        </Help>
        {supports.offsets ? (
          <OffsetControl
            id="x10-ambient-offset"
            label={t("ambient_offset", "Ambient probe offset")}
            limit={AMBIENT_OFFSET_LIMIT}
            current={current.ambientOffset}
            value={draft.ambientOffset}
            disabled={controlsDisabled}
            onChange={value => setDraft({ ...draft, ambientOffset: value })}
          />
        ) : (
          <Meta>
            {t(
              blockedText("FIRMWARE_UNSUPPORTED", null).key,
              blockedText("FIRMWARE_UNSUPPORTED", null).fallback
            )}
          </Meta>
        )}
      </Section>

      <Section>
        <SectionHeader>
          <BodyHighlight>{t("fail_mode", "Cooling fail mode")}</BodyHighlight>
          <SyncStatus configKey="V_FAIL_MODE" change={configuration.coolingFailMode.lastChange} />
        </SectionHeader>
        <Help>
          {t(
            "fail_mode_help",
            "Runs the compressor from the ambient temperature when the vessel probe cannot be trusted. If you turn it off and the probe is still faulty, the unit turns it back on within about 10 minutes."
          )}
        </Help>
        {supports.failMode ? (
          <div className="flex flex-wrap items-center gap-md">
            <OnOffSwitch
              label={t("fail_mode", "Cooling fail mode")}
              value={draft.coolingFailMode ?? failModePending ?? failModeReported}
              onText={t("on", "On")}
              offText={t("off", "Off")}
              disabled={controlsDisabled}
              onChange={value => setDraft({ ...draft, coolingFailMode: value })}
            />
            <Description>{failModeText}</Description>
          </div>
        ) : (
          <Meta>
            {t(
              blockedText("FIRMWARE_UNSUPPORTED", null).key,
              blockedText("FIRMWARE_UNSUPPORTED", null).fallback
            )}
          </Meta>
        )}
      </Section>

      <Section>
        <SectionHeader>
          <BodyHighlight>{t("halo", "Halo light")}</BodyHighlight>
          <SyncStatus configKey="HALO" change={configuration.halo.lastChange} />
        </SectionHeader>
        <Help>
          {t(
            "halo_help",
            "Turns the halo light on the tower on or off. The owner app can change it too."
          )}
        </Help>
        <OnOffSwitch
          label={t("halo", "Halo light")}
          value={draft.halo ?? current.halo}
          onText={t("on", "On")}
          offText={t("off", "Off")}
          disabled={controlsDisabled}
          onChange={value => setDraft({ ...draft, halo: value })}
        />
      </Section>

      <Section>
        <SectionHeader>
          <BodyHighlight>{t("volume", "Keg volume")}</BodyHighlight>
          <SyncStatus configKey="VOLUME_LEFT" change={configuration.volume.lastChange} />
        </SectionHeader>
        <Help>
          {t(
            "volume_help",
            "Overwrites the remaining volume of the mounted keg, from 1 to 10 L. Lowering it counts the difference as consumption. The unit starts again from 10 L at the next keg change."
          )}
        </Help>
        {supports.volume ? (
          <div className="flex flex-wrap items-center gap-md">
            <VolumeSpinner
              aria-label={t("volume", "Keg volume")}
              min={VOLUME_MIN_LITERS}
              max={VOLUME_MAX_LITERS}
              step={STEP}
              value={draft.volumeLeftLiters ?? volumePending ?? current.volumeLeftLiters}
              displayFormat={value => roundToStep(value).toFixed(1)}
              disabled={controlsDisabled}
              onChange={value =>
                setDraft({
                  ...draft,
                  volumeLeftLiters: clamp(roundToStep(value), VOLUME_MIN_LITERS, VOLUME_MAX_LITERS)
                })
              }
            />
            <span>L</span>
            <Description>{kegNowText}</Description>
            {draft.volumeLeftLiters !== undefined && (
              <Button inline onClick={() => setDraft({ ...draft, volumeLeftLiters: undefined })}>
                {t("keep_volume", "Keep the current volume")}
              </Button>
            )}
          </div>
        ) : (
          <Meta>
            {t(blockedText("NO_ACTIVE_KEG", null).key, blockedText("NO_ACTIVE_KEG", null).fallback)}
          </Meta>
        )}
      </Section>

      {!readOnly && (
        <ApplyBar>
          {dirty ? (
            <Chip tone="neutral">{pendingText}</Chip>
          ) : (
            <Description>{t("no_changes", "No changes")}</Description>
          )}
          <Button inline disabled={!dirty || applying} onClick={() => setDraft({})}>
            {t("discard", "Discard")}
          </Button>
          <Button
            primary
            disabled={!dirty || applying || !configuration.canApply}
            onClick={() => setConfirming(true)}
          >
            {applying ? t("sending", "Sending…") : t("send_to_unit", "Send to unit")}
          </Button>
        </ApplyBar>
      )}

      <Section>
        <Subtitle className="pb-sm">{t("history", "History")}</Subtitle>
        <ConfigurationHistory history={history} />
      </Section>

      <Modal
        className="modal-component-item"
        hasCloseButton={false}
        title={t("confirm_title", "Send to the unit?")}
        visible={confirming}
        onClose={() => setConfirming(false)}
      >
        <div className="confirmation-modal">
          <div className="confirmation-modal-question">
            <ul className="list-disc pl-lg pb-sm">
              {changes.map(change => (
                <li key={change}>{change}</li>
              ))}
            </ul>
            <Description>
              {t(
                "confirm_note",
                "The unit applies the change at once. It does not report offsets back, so check the temperatures afterwards."
              )}
            </Description>
          </div>
          <div className="confirmation-modal-options">
            <Button
              className="confirmation-modal-options-cancel"
              inline
              onClick={() => setConfirming(false)}
            >
              {t("cancel", "Cancel")}
            </Button>
            <Button primary onClick={send}>
              {t("send", "Send")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Configuration;
